import axios, { CancelToken } from 'axios';
import { API_BASE_URL, ContentTypes, getAxiosRequestConfig } from './apiBase';
import User from '../models/iUser';
import { IUserSearchApiResponse } from '../models/iUserSearchApiResponse';

/**
 * Gets the roles the user account is associated with.
 * @param authToken
 * @returns An array of the roles the user has.
 */
export const Login = async (authToken: string): Promise<string[]> => {
  const response = await axios.get<string[]>(
    `${API_BASE_URL}/Identity/login`,
    getAxiosRequestConfig(authToken)
  );
  return response.data;
};

/**
 * Gets all users of the application
 * @param authToken
 * @returns An array of the users.
 */
export const getAllUsers = async (authToken: string): Promise<User[]> => {
  const response = await axios.get<User[]>(
    `${API_BASE_URL}/Identity/users`,
    getAxiosRequestConfig(authToken)
  );
  return response.data;
};

/**
 * Gets all available user roles
 * @param authToken
 * @returns An array of the roles as String.
 */
export const getRoles = async (authToken: string): Promise<string[]> => {
  const response = await axios.get<string[]>(
    `${API_BASE_URL}/Identity/roles`,
    getAxiosRequestConfig(authToken)
  );
  return response.data;
};

/**
 * Gets users of the application by role
 * @param role as string the user role
 * @param authToken
 * @returns An array of the users.
 */
export const getUsersByRole = async (
  role: string,
  authToken: string
): Promise<User[]> => {
  const response = await axios.get<User[]>(
    `${API_BASE_URL}/Identity/users/${role}`,
    getAxiosRequestConfig(authToken)
  );
  return response.data;
};

/**
 * Searches for users based on the provided filters
 * @param authToken
 * @param searchFilter
 * @param pageNumber
 * @param pageSize
 * @param sortBy
 * @param sortDescending
 * @param skip
 * @returns The search results.
 */
export const searchUsers = async (
  authToken: string,
  searchFilter: { FirstName?: string; LastName?: string; Email?: string },
  pageNumber?: number,
  pageSize?: number,
  sortBy?: string,
  sortDescending?: boolean,
  skip?: number
): Promise<IUserSearchApiResponse<User>> => {
  if (
    !searchFilter.FirstName &&
    !searchFilter.LastName &&
    !searchFilter.Email
  ) {
    throw new Error('At least one SearchFilter variable is required.');
  }

  const params: { [key: string]: any } = {
    'SearchFilter.FirstName': searchFilter.FirstName,
    'SearchFilter.LastName': searchFilter.LastName,
    'SearchFilter.Email': searchFilter.Email,
    PageNumber: pageNumber,
    PageSize: pageSize,
    SortBy: sortBy,
    SortDescending: sortDescending,
    Skip: skip,
  };

  // Remove undefined or empty string parameters
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined || params[key] === '') {
      delete params[key];
    }
  });

  const response = await axios.get<IUserSearchApiResponse<User>>(
    `${API_BASE_URL}/Identity/users/search`,
    getAxiosRequestConfig(authToken, params)
  );
  return response.data;
};

/**
 * Adds a role to the specific user
 * @param userId: the user id as a string
 * @param role as string the user role
 * @param authToken
 * @returns the new user object.
 */
export const addRoleToUser = async (
  userId: string,
  role: string,
  authToken: string,
  cancelToken?: CancelToken
): Promise<User> => {
  const config = {
    ...getAxiosRequestConfig(authToken, null, ContentTypes.Json),
    cancelToken,
  };
  const response = await axios.post<User>(
    `${API_BASE_URL}/Identity/userRole/${userId}/${role}`,
    null,
    config
  );
  return response.data;
};

/**
 * removes a role to the specific user
 * @param userId: the user id as a string
 * @param role as string the user role
 * @param authToken
 * @returns the new user object.
 */
export const removeRoleFromUser = async (
  userId: string,
  role: string,
  authToken: string
): Promise<User> => {
  const response = await axios.delete<User>(
    `${API_BASE_URL}/Identity/userRole/${userId}/${role}`,
    getAxiosRequestConfig(authToken)
  );
  return response.data;
};
